import React, {
    useState,
    useEffect,
} from "react";
import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    Stack,
    Snackbar,
    Alert,
    MenuItem,
    FormControl,
    Select,
    InputLabel,
    CircularProgress,
    Tabs,
    Tab,
    TextField,
} from "@mui/material";
import Editor from "@monaco-editor/react";

// Helper: safely parse HTML into a DOM Document (mainly for getting body inner HTML if needed)
function parseHtmlString(html) {
    const parser = new DOMParser();
    return parser.parseFromString(html, "text/html");
}

export default function TemplateEditor() {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [fullHtml, setFullHtml] = useState("");
    const [extraPrompt, setExtraPrompt] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [viewMode, setViewMode] = useState(0); // 0 = Code View, 1 = Preview Mode
    const [snack, setSnack] = useState({
        open: false,
        msg: "",
        sev: "success",
    });

    const API_BASE_URL =
        process.env.REACT_APP_FASTAPI_URL || "https://docmorph.ivldsp.in";

    // Fetch templates list
    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/ai/templates`);
                const data = await res.json();
                setTemplates(data.templates || []);
            } catch (err) {
                console.error("Error fetching templates:", err);
            }
        };
        fetchTemplates();
    }, [API_BASE_URL]);

    // Handle template selection
    const handleTemplateChange = async (e) => {
        const filename = e.target.value;
        setSelectedTemplate(filename);
        if (!filename) {
            setFullHtml("");
            setExtraPrompt("");
            return;
        }

        setLoading(true);
        try {
            // Fetch HTML
            const res = await fetch(`${API_BASE_URL}/templates/${filename}`);
            const html = await res.text();
            setFullHtml(html);

            // Fetch extra prompt
            const promptRes = await fetch(`${API_BASE_URL}/api/ai/template-prompt/${filename}`);
            const promptData = await promptRes.json();
            if (promptData.success) {
                setExtraPrompt(promptData.prompt || "");
            }
        } catch (err) {
            console.error("Error fetching template content:", err);
            setSnack({ open: true, msg: "Failed to load template", sev: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Code Editor Change
    const handleCodeChange = (value) => {
        setFullHtml(value || "");
    };

    // Switch tabs
    const handleTabChange = (event, newValue) => {
        setViewMode(newValue);
    };

    const onSave = async () => {
        if (!selectedTemplate) return;

        setSaving(true);
        try {
            // Save HTML
            const res = await fetch(`${API_BASE_URL}/api/ai/savetemplate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    filename: selectedTemplate,
                    html: fullHtml,
                }),
            });
            const data = await res.json();
            if (!data.success) {
                throw new Error(data.error || "Failed to save template");
            }

            // Save Extra Prompt
            const promptRes = await fetch(`${API_BASE_URL}/api/ai/save-template-prompt`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    filename: selectedTemplate,
                    prompt: extraPrompt,
                }),
            });
            const promptData = await promptRes.json();
            if (!promptData.success) {
                throw new Error(promptData.error || "Failed to save extra prompt");
            }

            setSnack({ open: true, msg: "Template and prompt saved successfully!", sev: "success" });
        } catch (e) {
            console.error("Save error:", e);
            setSnack({ open: true, msg: e.message, sev: "error" });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ p: 3, height: "100vh", width: "100%", backgroundColor: "#f4f6f8", display: "flex", flexDirection: "column", alignItems: "center", boxSizing: "border-box" }}>
            <Typography variant="h5" gutterBottom sx={{ width: "100%", textAlign: "center", mb: 2 }}>
                Template Editor
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 2, width: "100%", px: 2, alignItems: "center" }}>
                <FormControl sx={{ minWidth: 300 }}>
                    <InputLabel>Select Template</InputLabel>
                    <Select value={selectedTemplate} label="Select Template" onChange={handleTemplateChange}>
                        {templates.map((tpl) => (
                            <MenuItem key={tpl.name} value={tpl.name}>
                                {tpl.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Button variant="contained" color="primary" onClick={onSave} disabled={!selectedTemplate || saving}>
                    {saving ? <CircularProgress size={24} color="inherit" /> : "Save Template"}
                </Button>
            </Stack>

            {selectedTemplate && (
                <Box sx={{ width: "100%", px: 2, mb: 2 }}>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Extra Prompt for AI"
                        variant="outlined"
                        value={extraPrompt}
                        onChange={(e) => setExtraPrompt(e.target.value)}
                        placeholder="Enter extra instructions for Gemini refinement..."
                        sx={{ backgroundColor: "#fff" }}
                    />
                </Box>
            )}

            {selectedTemplate && (
                <Paper sx={{ width: "100%", mb: 2 }}>
                    <Tabs value={viewMode} onChange={handleTabChange} centered>
                        <Tab label="Code Editor" />
                        <Tab label="Preview Mode" />
                    </Tabs>
                </Paper>
            )}

            {loading ? (
                <CircularProgress sx={{ mt: 5 }} />
            ) : selectedTemplate ? (
                <Grid container spacing={2} sx={{ width: "100%", px: 2, flexGrow: 1, minHeight: 0, mb: 2 }}>
                    <Grid item xs={12} sx={{ display: "flex", flexDirection: "column", minWidth: "100%" }}>
                        <Paper elevation={3} sx={{ flex: 1, borderRadius: 2, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                            <Typography variant="subtitle1" sx={{ p: 1.5, pl: 2, bgcolor: "#1e1e1e", color: "#fff", fontWeight: 600 }}>
                                {viewMode === 0 ? "HTML Code Editor" : "Live Template Preview"}
                            </Typography>
                            <Box sx={{ flex: 1, p: 0, minHeight: 0 }}>
                                {viewMode === 0 ? (
                                    <Editor
                                        height="100%"
                                        defaultLanguage="html"
                                        value={fullHtml}
                                        onChange={handleCodeChange}
                                        theme="vs-dark"
                                        options={{
                                            minimap: { enabled: false },
                                            fontSize: 14,
                                            wordWrap: "on",
                                            formatOnPaste: true,
                                            formatOnType: true,
                                        }}
                                    />
                                ) : (
                                    <Box sx={{ height: "100%", bgcolor: "#fff" }}>
                                        <iframe
                                            title="Template Preview"
                                            srcDoc={fullHtml}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                border: "none",
                                            }}
                                            sandbox="allow-same-origin allow-scripts"
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            ) : (
                <Typography variant="body1" color="textSecondary" sx={{ mt: 5 }}>
                    Please select a template to start editing.
                </Typography>
            )}

            <Snackbar open={snack.open} autoHideDuration={2500} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
                <Alert severity={snack.sev} sx={{ width: "100%" }}>
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Box>
    );
}
